import { isTodayWorkClient } from './clientAttribution'

interface DownloadURLResult {
  success: boolean
  error?: string
}

interface TodayWorkShellBridge {
  downloadURL: (url: string) => Promise<DownloadURLResult>
}

type TodayWorkWindow = Window & {
  todayWorkShell?: TodayWorkShellBridge
}

/**
 * 让 TodayWork 桌面壳接管远程文件下载。
 *
 * 大照片压缩包不能在页面里先 fetch 成 Blob，否则会占用与文件等量的渲染进程内存；
 * 壳接管后由 Chromium 直接把响应流写入系统下载目录。旧 AI 客户端交给系统浏览器，
 * 避免退回跨域链接导航后让业务 iframe 白屏；只有非 AI 客户端返回 false。
 *
 * @param url - HTTP(S) 文件地址
 * @returns 当前下载是否已由 AI 客户端接管或交给系统浏览器
 *
 * @example
 * ```ts
 * const handled = await downloadWithTodayWorkShell(fileURL)
 * if (!handled) window.location.href = fileURL
 * ```
 */
export async function downloadWithTodayWorkShell(
  url: string
): Promise<boolean> {
  if (!isTodayWorkClient()) return false

  const bridge = (window as TodayWorkWindow).todayWorkShell
  if (!bridge) {
    // 旧壳没有下载桥，但已有新窗口外链处理。校验范围与主进程一致，不能打开本机或任意协议。
    const target = new URL(url, window.location.href)
    if (
      !['https:', 'http:'].includes(target.protocol) ||
      ['localhost', '[::1]', '0.0.0.0'].includes(target.hostname) ||
      target.hostname.startsWith('127.') ||
      target.hostname.endsWith('.localhost') ||
      target.username ||
      target.password
    ) {
      throw new Error('下载地址不受支持')
    }
    // 壳拦截新窗口后返回 null 属于正常行为，不能据此再导航当前 iframe。
    window.open(target.toString(), '_blank', 'noopener,noreferrer')
    return true
  }

  const result = await bridge.downloadURL(url)
  if (!result.success) {
    throw new Error(result.error || '下载请求未被客户端接受')
  }
  return true
}

/**
 * 打开站内页面；TodayWork 内嵌页在当前 iframe 内跳转，其余环境保持新窗口行为。
 *
 * @param path - 站内绝对路径，必须以 `/` 开头
 * @returns 无返回值
 *
 * @example
 * ```ts
 * openInternalPage('/attendance/export-list?fromPlace=photoAll')
 * ```
 */
export function openInternalPage(path: string): void {
  if (isTodayWorkClient()) {
    window.location.assign(path)
    return
  }
  window.open(path)
}
